import React, { useCallback, useMemo } from 'react';
import { MAPPING_FIELDS, PRODUCT_FIELD_OPTIONS } from './configurationConstants';
import { useConfigurationContext } from './Context/ConfigurationContext';
import {
  MappingFieldLabel,
  MappingSourceCell,
  MappingValueCell,
  displayValueFor,
} from './MappingRow';
import { MappingValue, FieldConfig, MappingField } from './types';
import { getFieldError } from './validationUtils';
import Table from 'Components/DesignSystem/Table/Table';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';

type MappingTableProps = {
  id?: string;
  mappings: { [key: string]: MappingValue };
};

const createFieldConfigMap = (fields: FieldConfig[]): Map<string, FieldConfig> => {
  return new Map(fields.map((field) => [field.key, field]));
};

/**
 * Configuration → Products → Mapping: which connector attribute feeds which
 * SmartStudio field.
 *
 * On DS `Table` since § 133. Every handler is unchanged — `handleSourceChange` still
 * pre-fills `fixedValue` when the source becomes `Fixed Value` and blanks the value
 * otherwise, and `updateProductMapping` receives the same three arguments.
 *
 * The table it replaces was hand-rolled and had two real problems on top of the
 * hardcoded `tw-bg-zinc-200` header:
 *
 * - **Striping was computed from the row INDEX and painted per cell**
 *   (`isEvenRow ? 'tw-bg-white' : 'tw-bg-gray-100'`, applied to each `<td>`
 *   separately). Three cells each deciding their own background is three chances to
 *   disagree; `striped` states it once for the row.
 * - **`tw-max-w-0` + `tw-truncate` on the label and value cells.** That truncates
 *   without a width to truncate against, so how much text survived depended on the
 *   table's own layout pass — and the only recovery was a `title` tooltip, which
 *   touch users do not have.
 */
export const MappingTable = ({ id = 'mapping-table', mappings }: MappingTableProps) => {
  const { isEditMode, updateProductMapping, validationErrors } = useConfigurationContext();

  const fieldConfigMap = useMemo(() => createFieldConfigMap(MAPPING_FIELDS), []);

  const handleSourceChange = useCallback((fieldKey: string, source: string) => {
    const fieldConfig = fieldConfigMap.get(fieldKey);
    const newValue = source === 'Fixed Value' && fieldConfig?.fixedValue
      ? fieldConfig.fixedValue
      : '';

    updateProductMapping(fieldKey as MappingField, source, newValue);
  }, [fieldConfigMap, updateProductMapping]);

  const handleValueChange = useCallback((fieldKey: string, value: string) => {
    const currentMapping = mappings[fieldKey];
    updateProductMapping(fieldKey as MappingField, currentMapping?.source || '', value);
  }, [mappings, updateProductMapping]);

  const columns: TableColumn<FieldConfig>[] = [
    {
      id: 'field',
      header: 'SmartStudio Field',
      accessor: 'label',
      width: '30%',
      render: (_value, fieldConfig) => (
        <MappingFieldLabel rowId={`${id}-row-${fieldConfig.key}`} fieldConfig={fieldConfig} />
      ),
    },
    {
      id: 'source',
      header: 'Source',
      accessor: 'key',
      width: '30%',
      cellType: isEditMode ? 'dropdown' : 'text',
      render: (_value, fieldConfig) => {
        const rowId = `${id}-row-${fieldConfig.key}`;
        const mapping = mappings[fieldConfig.key];
        if (!isEditMode) {
          return (
            <Typography variant="body-primary" as="span" data-testid={`${rowId}-source`}>
              {mapping?.source ?? '-'}
            </Typography>
          );
        }
        return (
          <MappingSourceCell
            rowId={rowId}
            fieldConfig={fieldConfig}
            mapping={mapping}
            onSourceChange={handleSourceChange}
            hasSourceError={!!getFieldError(validationErrors, `${fieldConfig.key}.source`)}
          />
        );
      },
    },
    {
      id: 'value',
      header: 'Value',
      accessor: 'key',
      width: '40%',
      render: (_value, fieldConfig) => {
        const rowId = `${id}-row-${fieldConfig.key}`;
        const mapping = mappings[fieldConfig.key];
        if (!isEditMode) {
          const shown = displayValueFor(fieldConfig, mapping);
          return (
            <Typography
              variant="body-primary"
              as="span"
              title={shown}
              data-testid={`${rowId}-value`}
            >
              {shown || '-'}
            </Typography>
          );
        }
        return (
          <MappingValueCell
            rowId={rowId}
            fieldConfig={fieldConfig}
            mapping={mapping}
            fieldOptions={PRODUCT_FIELD_OPTIONS}
            onValueChange={handleValueChange}
            hasValueError={!!getFieldError(validationErrors, `${fieldConfig.key}.value`)}
          />
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      data={MAPPING_FIELDS}
      rowKey="key"
      headerVariant="light"
      striped
      bordered
      data-testid={id}
    />
  );
};
